import { useMemo } from 'react';
import { calculateNodeOutputSchema } from '../utils/schemaUtils';

/**
 * Hook to dynamically calculate the input schema for a node based on its connections.
 * This replaces the need to store 'inputSchema' and 'outputSchema' in the node data.
 * 
 * @param {string} nodeId - The ID of the node to calculate schema for
 * @param {Array} nodes - All nodes in the flow
 * @param {Array} edges - All edges in the flow
 * @returns {Array} The calculated schema (array of column definitions)
 */
export const useNodeSchema = (nodeId, nodes, edges) => {
    // Memoize the calculation to prevent expensive re-runs
    return useMemo(() => {
        if (!nodeId || !nodes || !edges) return [];

        // To get Input Schema for 'nodeId', we need the Output Schema of its parent(s).
        const incomingEdges = edges.filter(e => e.target === nodeId);
        if (incomingEdges.length === 0) return [];

        // Handle multiple inputs (e.g. Join)
        if (incomingEdges.length > 1) {
             const node = nodes.find(n => n.id === nodeId);
             if (node?.data?.nodeType === 'join') {
                 // For Join UI, we often need to see columns from both sides
                 // Return all available columns from all inputs flattened
                 const schemas = incomingEdges.map(e => calculateNodeOutputSchema(e.source, nodes, edges));
                 return schemas.flat(); 
             }
        }

        // Default: Get output schema of the source node of the first incoming edge
        return calculateNodeOutputSchema(incomingEdges[0].source, nodes, edges);

    }, [nodeId, nodes, edges]);
};

